import * as React from 'react';
import { RechnungPositionFilter, useRechnungPositionList } from 'api/rechnungPosition';
import { useRechnungList } from 'api/rechnung';
import { useArtikelList } from 'api/artikel';
import { ArtikelumsatzSummary, defaultLineData, FilterValues, LineData } from '../types';

export interface ArtikelumsatzSummaryProps extends FilterValues {
    enabled: boolean;
}

export const useArtikelumsatzSummary = ({
    datumVon,
    datumBis,
    artikelGruppeNr,
    artikelNr,
}: ArtikelumsatzSummaryProps): ArtikelumsatzSummary => {
    const artikelList = useArtikelList({
        artikelGruppeNr,
        artikelNr,
    });

    const rechnungList = useRechnungList({
        datumVon,
        datumBis,
        withKunde: true,
    });

    const rechnungPositionListFilter = React.useMemo<RechnungPositionFilter>(
        () => ({
            rNrList: (rechnungList.data || []).map((a) => a.nr),
            artikelGruppeNr,
            artikelNr,
        }),
        [rechnungList.data, artikelGruppeNr, artikelNr],
    );

    const rechnungPositionList = useRechnungPositionList(rechnungPositionListFilter, {
        enabled: !!rechnungList.data,
    });

    return React.useMemo<ArtikelumsatzSummary>(() => {
        if (!artikelList.data || !rechnungList.data || !rechnungPositionList.data) {
            return {
                loading: true,
                lines: [],
                totalMenge: 0,
                totalAufwand: 0,
                totalUmsatz: 0,
            };
        }

        let totalMenge = 0;
        let totalAufwand = 0;
        let totalUmsatz = 0;

        const byKundeNr: { [key: number]: LineData } = {};

        (rechnungPositionList.data || []).forEach((rechnungPosition) => {
            const rechnung = (rechnungList.data || []).find((a) => a.nr === rechnungPosition.rNr);
            if (!rechnung) {
                return;
            }

            if (!rechnung.kNr) {
                return;
            }

            if (!byKundeNr[rechnung.kNr]) {
                byKundeNr[rechnung.kNr] = {
                    ...defaultLineData,
                    kNr: rechnung.kNr,
                    kName: rechnung.kunde?.name1 || '?',
                };
            }

            if (rechnung.kNr) {
                byKundeNr[rechnung.kNr].artikelGruppeNr = rechnungPosition.artikelGruppeNr;
                byKundeNr[rechnung.kNr].artikelNr = rechnungPosition.artikelNr;
                byKundeNr[rechnung.kNr].artikelText = artikelList.data?.[0]?.text || '?';
                byKundeNr[rechnung.kNr].reclayProduktgruppe = rechnung.kunde?.reclayProduktgruppe || '';
                byKundeNr[rechnung.kNr].einheit = artikelList.data?.[0]?.einheit || '?';

                const aufwand = rechnungPosition.posMenge * rechnungPosition.posEkPreis;

                byKundeNr[rechnung.kNr].menge += rechnungPosition.posMenge;
                byKundeNr[rechnung.kNr].umsatz += rechnungPosition.posNetto;
                byKundeNr[rechnung.kNr].aufwand += aufwand;
                // byKundeNr[rechnung.kNr].bMarge +=
                //     rechnungPosition.posNetto - rechnungPosition.posMenge * rechnungPosition.posEkPreis;

                totalMenge += rechnungPosition.posMenge;
                totalUmsatz += rechnungPosition.posNetto;
                totalAufwand += aufwand;
            }
        });

        return {
            loading: artikelList.isLoading || rechnungList.isLoading || rechnungPositionList.isLoading,
            lines: Object.values(byKundeNr).sort((a, b) => a.kName.localeCompare(b.kName)),
            totalMenge,
            totalAufwand,
            totalUmsatz,
        };
    }, [artikelList, rechnungList, rechnungPositionList]);
};
